import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { LayoutService } from "./service/app.layout.service";
import { MsalService } from '@azure/msal-angular';
import { CompanyWithRolesAndWorkCentersDTO, RoleDTO, User, UserDTO } from 'src/app/interfaces/base-entity';
import { StorageService } from '../shared/services/storage.service';
import { SgsstAuthUserService } from '../shared/services/auth.service';
import { SessionLogoutService } from '../shared/services/session-logout.service';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subscription } from 'rxjs';
import { BlockUIService } from '../shared/services/blockUI.service';
import { Route, Router } from '@angular/router';
import { NotificationService } from '../notifications/services/notification.service';
import { SignalRService } from 'src/app/utils/SignalR.service';
import { environment } from 'src/environments/environment';
import { UserRoleEnum, StorageKeys } from 'src/app/enums/enums';
import { CompanyService } from '../administration/services/company.service';
import { UserRoleService } from 'src/app/utils/user-role.service';
import { TranslationService } from '../../services/translation.service';
import { resolveWorkCenterId } from 'src/app/utils/tenant-context.util';
import {
    clearTenantContext,
    hasValidTenantContext,
    isTenantContextBlocked,
    isTenantSessionBootstrapping,
    persistTenantContext as writeTenantContext,
    readTenantPreference,
    resolveSessionTenantContext,
    resolveTenantContextFromCompanyList,
    selectBestTenantRole,
    SessionTenantContext
} from 'src/app/utils/session-tenant-context.util';

@Component({
    selector: 'app-topbar',
    templateUrl: './app.topbar.component.html',
    styleUrls: ['./app.topbar.component.scss']
})
export class AppTopBarComponent implements OnInit, OnDestroy {

    items!: MenuItem[];
    signalREndpoint = `${environment.host}/notificationHub`;
    user: UserDTO | null = null;
    @ViewChild('menubutton') menuButton!: ElementRef;

    @ViewChild('topbarmenubutton') topbarMenuButton!: ElementRef;

    @ViewChild('topbarmenu') menu!: ElementRef;

    suscribers = new Subscription();
    @ViewChild('op') overlayPanel!: OverlayPanel;

    selectedCompany: CompanyWithRolesAndWorkCentersDTO | null = null;
    selectedWorkcenter: number = 0;
    notificationsCount: any;
    rolesEnum = UserRoleEnum;
    companyList: CompanyWithRolesAndWorkCentersDTO[] = [];
    modalSwitch: boolean = false;
    currentCompany: CompanyWithRolesAndWorkCentersDTO | null = null;
    currentRole: RoleDTO | null = null;
    public blockUI: BlockUIService = inject(BlockUIService);
    public notificationService = inject(NotificationService);
    private signalRService = inject(SignalRService);
    private readonly messageService = inject(MessageService);

    public userRoleService: UserRoleService = inject(UserRoleService);

    constructor(public layoutService: LayoutService,
        private authService: MsalService,
        private storage: StorageService,
        public authSsgtService: SgsstAuthUserService,
        private sessionLogout: SessionLogoutService,
        private route: Router,
        private companyService: CompanyService,
        private translationService: TranslationService) { }

    ngAfterViewInit(): void {

    }

    isAdminWorkcenter(): boolean {
        return this.userRoleService.getRoleEnum() === this.rolesEnum.AdminWorkCenter;
    }

    get isSessionBootstrapping(): boolean {
        return isTenantSessionBootstrapping({
            msalHasAccount: this.authService.instance.getAllAccounts().length > 0,
            resolutionPending: this.authSsgtService.isTenantResolutionPending
        });
    }

    get hasMissingCompanyContext(): boolean {
        return isTenantContextBlocked(this.storage, {
            liveUser: this.user,
            msalHasAccount: this.authService.instance.getAllAccounts().length > 0,
            resolutionPending: this.authSsgtService.isTenantResolutionPending
        });
    }

    ngOnDestroy(): void {
        this.suscribers?.unsubscribe();
        this.signalRService.stopConnection();
    }

    ngOnInit() {
        this.refreshTenantContext();
        this.translateCompanyRoles().catch(console.error);
        this.refreshMenuItems();
        this.ensureRealtimeIfTenantReady();

        this.suscribers.add(this.authSsgtService.userLoggedIn$.subscribe((user) => {
            this.user = user;

            if (!user) {
                return;
            }

            this.refreshTenantContext(user);
            this.refreshMenuItems();
            this.ensureRealtimeIfTenantReady();
        }));

        this.suscribers.add(this.authSsgtService.tenantResolutionState$.subscribe(() => {
            this.ensureRealtimeIfTenantReady();
        }));

        this.notificationService.notificationsInfo$.subscribe({
            next: (data: any) => {
                if (data && data.info) {
                    this.notificationsCount = data.info;
                }
            },
            error: (error: any) => {
            }
        });
    }

    private ensureRealtimeIfTenantReady(): void {
        if (this.realtimeBound) {
            return;
        }
        if (!hasValidTenantContext(this.storage) || this.authSsgtService.isTenantResolutionPending) {
            return;
        }

        this.realtimeBound = true;
        this.signalRService.startConnection(this.signalREndpoint);

        this.signalRService.on('notificationInfo', (res: any) => {
            if (res && res.info.success) {
                this.notificationsCount = res.info.data;
                this.notificationService.notificationsInfo$.next({ info: this.notificationsCount, type: res.type, data: res.data });
            }
        });

        this.signalRService.on('notification', (id: number, message: string, title: string, link?: string) => {
            this.refreshNotificationsCount();
            const isTar = this.isTarNotification(title, link);
            const detail = this.toPlainNotificationText(message) || 'Tienes una nueva notificación.';
            this.messageService.add({
                severity: isTar ? 'warn' : 'info',
                summary: title || 'Nueva notificación',
                detail: detail.length > 180 ? `${detail.slice(0, 180)}…` : detail,
                life: 8000,
                data: { link, id }
            });
        });

        this.signalRService.on('signal', (res: any) => {
            console.log('SignalR signal received:', res);
        });
    }

    private refreshMenuItems(): void {
        const hasMultipleCompanies = this.companyList.length > 1;
        this.items = [
            { label: 'Cambiar Compañia', icon: 'pi pi-building', command: () => this.openSwtichCompany(), visible: hasMultipleCompanies },
            { label: 'Perfil', icon: 'pi pi-user-edit', command: () => this.route.navigate(['/profile']) },
            { label: 'Salir', icon: 'pi pi-sign-out', command: () => this.logout() }
        ];
    }

    private realtimeBound = false;

    private persistTenantContext(context: SessionTenantContext): void {
        writeTenantContext(this.storage, context);
        this.selectedWorkcenter = context.workCenterId ?? 0;
    }

    private refreshTenantContext(user?: UserDTO | null): void {
        const sessionUser =
            user ??
            this.user ??
            (this.storage.getItem(StorageKeys.CurrentUser, true) as UserDTO | null);

        if (sessionUser) {
            // Respetar compañía/rol ya elegidos (p. ej. "Cambiar compañía");
            // si no son válidos para el usuario, cae al mejor tenant disponible.
            const context = resolveSessionTenantContext(sessionUser, readTenantPreference(this.storage));
            if (context) {
                this.persistTenantContext(context);
                this.companyList = context.companyList;
                this.currentCompany = context.company;
                this.currentRole = context.role;
                this.selectedCompany = context.company;
                this.selectedWorkcenter = context.workCenterId ?? 0;
                return;
            }

            clearTenantContext(this.storage);
            this.companyList = [];
            this.currentCompany = null;
            this.currentRole = null;
            this.selectedCompany = null;
            this.selectedWorkcenter = 0;
            return;
        }

        const storedCompanyList = this.storage.getItem('companyList', false) as CompanyWithRolesAndWorkCentersDTO[] | null;
        const storedCompany = this.storage.getItem('currentCompany', false) as CompanyWithRolesAndWorkCentersDTO | null;
        const storedRole = this.storage.getItem('currentRole', false) as RoleDTO | null;
        const selectedWorkCenterId = this.storage.getItem('selectedWorkCenterId', false);

        this.companyList = storedCompanyList || [];
        this.currentCompany = storedCompany;
        this.currentRole = storedRole;
        this.selectedCompany = storedCompany;
        this.selectedWorkcenter = typeof selectedWorkCenterId === 'number' ? selectedWorkCenterId : Number(selectedWorkCenterId) || 0;

        if (!this.currentCompany?.companyId || !this.currentRole?.roleId) {
            const context = resolveTenantContextFromCompanyList(this.companyList);
            if (context) {
                this.persistTenantContext(context);
                this.companyList = context.companyList;
                this.currentCompany = context.company;
                this.currentRole = context.role;
                this.selectedCompany = context.company;
                this.selectedWorkcenter = context.workCenterId ?? 0;
            }
        }
    }

    retryTenantContext(): void {
        this.refreshTenantContext(this.user);
        this.refreshMenuItems();

        if (this.currentCompany?.companyId) {
            this.messageService.add({
                severity: 'success',
                summary: 'Contexto recuperado',
                detail: 'La compañía activa fue restaurada correctamente.'
            });
            return;
        }

        this.messageService.add({
            severity: 'warn',
            summary: 'Contexto no disponible',
            detail: 'No se encontró una compañía o rol activo para esta sesión. Inicie sesión nuevamente.'
        });
    }

    openNotificationFromToast(link?: string | null): void {
        if (!link?.trim()) {
            void this.route.navigate(['/notifications/view']);
            return;
        }
        const trimmed = link.trim();
        if (/^https?:\/\//i.test(trimmed)) {
            window.open(trimmed, '_blank', 'noopener');
            return;
        }
        void this.route.navigateByUrl(trimmed.startsWith('/') ? trimmed : `/${trimmed}`);
    }

    private refreshNotificationsCount(): void {
        this.notificationService.getNotificationsCount().subscribe({
            next: (response: any) => {
                if (response?.success && response.data) {
                    this.notificationsCount = response.data;
                    this.notificationService.notificationsInfo$.next({
                        info: this.notificationsCount,
                        type: 'notificationInfo'
                    });
                }
            },
            error: () => {
                // Silencioso: el badge se actualizará en el próximo ciclado.
            }
        });
    }

    private isTarNotification(title?: string, link?: string): boolean {
        const t = (title ?? '').toLowerCase();
        const l = link ?? '';
        return t.includes('tar') || l.includes('form-engine/execution') || l.includes('tar-assignment');
    }

    private toPlainNotificationText(message?: string): string {
        if (!message) {
            return '';
        }
        return message
            .replace(/<style[\s\S]*?<\/style>/gi, ' ')
            .replace(/<script[\s\S]*?<\/script>/gi, ' ')
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s*Abrir:\s*(?:https?:\/\/\S+|\/\S+)/gi, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    openSwtichCompany() {
        this.overlayPanel.hide();
        this.translateCompanyRoles().catch(console.error);
        this.modalSwitch = true;
    }

    private async translateCompanyRoles(): Promise<void> {
        for (const company of this.companyList) {
            if (company.roles) {
                company.roles = await this.translationService.translateRolesAsync(company.roles);
            }
        }
    }

    onSelectCompany(companyId: number) {
        const selectedCompany = this.companyList.find(c => c.companyId === companyId);
        if (!selectedCompany) {
            return;
        }

        const bestRole = selectBestTenantRole(selectedCompany.roles);
        if (!bestRole) {
            return;
        }

        this.storage.setItem('currentCompany', selectedCompany, false);
        this.storage.setItem('currentRole', bestRole, false);

        const wcId = resolveWorkCenterId(selectedCompany, bestRole);
        if (wcId) {
            this.storage.setItem('selectedWorkCenterId', wcId, false);
            this.selectedWorkcenter = wcId;
        } else {
            this.storage.remove('selectedWorkCenterId');
        }

        this.currentCompany = selectedCompany;
        this.selectedCompany = selectedCompany;
        this.currentRole = bestRole;
        this.modalSwitch = false;
        this.route.navigate(['/']);
        this.authSsgtService.userLoggedIn$.next(this.user!);
    }
    openUserMenu(event: any) {
        this.user = this.storage.getItem(StorageKeys.CurrentUser, true);
        this.overlayPanel.show(event);
    }

    logout() {
        this.sessionLogout.logout();
    }

    getCompanyLogoUrl(logoUrl: string | null | undefined): string {
        if (!logoUrl) {
            return './assets/images/intro-sgsst.png';
        }

        // Si es una data URL (base64), usarla directamente
        if (logoUrl.startsWith('data:image/')) {
            return logoUrl;
        }

        // Si es una URL relativa, construir la URL completa
        if (logoUrl.startsWith('./') || logoUrl.startsWith('/')) {
            return logoUrl;
        }

        // Si es una URL de blob storage, validar que existe
        if (logoUrl.includes('blob.core.windows.net')) {
            if (this.isValidBlobUrl(logoUrl)) {
                return logoUrl;
            } else {
                return './assets/images/intro-sgsst.png';
            }
        }
        // Por defecto, usar imagen por defecto
        return './assets/images/intro-sgsst.png';
    }

    getCorrectLogoUrl(company: any): string {
        if (!company) {
            return './assets/images/intro-sgsst.png';
        }

        const logoUrl = company?.logoUrl || company?.urlCompanyLogo;
        return this.getCompanyLogoUrl(logoUrl);
    }

    private isValidBlobUrl(url: string): boolean {
        try {
            const urlObj = new URL(url);
            const isValidHost = urlObj.hostname.includes('blob.core.windows.net');
            const hasCompanyLogos = urlObj.pathname.includes('CompanyLogos');
            const fileName = urlObj.pathname.split('/').pop();
            const hasFileName = Boolean(fileName && fileName.length > 0);

            return isValidHost && hasCompanyLogos && hasFileName;
        } catch (error) {
            return false;
        }
    }

    onImageError(event: any): void {
        event.target.src = './assets/images/intro-sgsst.png';
    }
}
